import type { ReconnectContext, ReconnectPolicy } from 'livekit-client'

// Максимальная задержка между попытками переподключения (в мс)
const MAX_RETRY_DELAY = 10000

// Задержки для мобильных: 20 попыток с агрессивным началом
const MOBILE_RETRY_DELAYS_IN_MS = [
  0, 200, 500, 1000, 1500, 2000, 3000, 4000, 5000, 6000,
  7000, 8000, 9000, 10000, 10000, 10000, 10000, 10000, 10000, 10000,
]

// Задержки для десктопа: стандартные 10 попыток
const DESKTOP_RETRY_DELAYS_IN_MS = [
  0, 300, 1200, 2700, 4800, 7000, 7000, 7000, 7000, 7000,
]

interface CreateMobileReconnectPolicyOptions {
  isMobile: boolean
  isInAppBrowser: boolean
}

interface MobileReconnectPolicyResult extends ReconnectPolicy {
  isMobile: boolean
  isInAppBrowser: boolean
}

/**
 * Сколько попыток SDK делает на том же Room, прежде чем сдаться.
 *
 * Две быстрые попытки закрывают короткие моргания сети. Если не помогло,
 * ActiveRoom получает Disconnected и запускает свой retry с новым Room.
 */
export const CAPPED_RECONNECT_MAX_ATTEMPTS = 2

const CAPPED_RETRY_DELAYS_IN_MS = [0, 200]

interface CreateCappedReconnectPolicyOptions {
  maxAttempts?: number
  /**
   * Если вернула false, SDK не делает следующую попытку на том же Room.
   *
   * Нужна, когда человек сам вышел или его отклонили: иначе политика
   * поднимает соединение снова, и гость снова висит в зале ожидания.
   */
  shouldReconnect?: () => boolean
}

/**
 * Собирает политику SDK с ограничением на две попытки того же Room.
 *
 * `shouldReconnect` читается на каждую попытку. Если вернула false,
 * delay не выдаётся — SDK считает реконнект исчерпанным.
 */
export function createCappedReconnectPolicy({
  maxAttempts = CAPPED_RECONNECT_MAX_ATTEMPTS,
  shouldReconnect,
}: CreateCappedReconnectPolicyOptions = {}): ReconnectPolicy {
  const retryDelays = CAPPED_RETRY_DELAYS_IN_MS.slice(0, maxAttempts)

  return {
    nextRetryDelayInMs(context: ReconnectContext): number | null {
      if (shouldReconnect && !shouldReconnect()) {
        console.log('[CappedReconnectPolicy] Reconnect skipped: shouldReconnect=false')

        return null
      }

      if (context.retryCount >= retryDelays.length) {
        console.log(`[CappedReconnectPolicy] Max retries (${retryDelays.length}) exceeded`)

        return null
      }

      const delay = retryDelays[context.retryCount]

      console.log(
        `[CappedReconnectPolicy] Retry ${context.retryCount + 1}/${retryDelays.length}, delay: ${delay}ms`,
      )

      return delay
    },
  }
}

/**
 * Политика с 10–20 попытками и растущей задержкой. Сейчас не используется:
 * оставлена на случай отката, если короткие моргания снова захотим закрывать
 * только силами SDK.
 */
export function createMobileReconnectPolicy({
  isMobile,
  isInAppBrowser,
}: CreateMobileReconnectPolicyOptions): MobileReconnectPolicyResult {
  // Выбираем политику
  const useMobilePolicy = isMobile || isInAppBrowser
  const retryDelays = useMobilePolicy 
    ? [...MOBILE_RETRY_DELAYS_IN_MS] 
    : [...DESKTOP_RETRY_DELAYS_IN_MS]

  if (useMobilePolicy) {
    console.log('[MobileReconnectPolicy] Using mobile reconnect policy (20 attempts)')
  }

  return {
    isMobile,
    isInAppBrowser,

    nextRetryDelayInMs(context: ReconnectContext): number | null {
      if (context.retryCount >= retryDelays.length) {
        console.log(`[MobileReconnectPolicy] Max retries (${retryDelays.length}) exceeded`)
        return null
      }

      const baseDelay = retryDelays[context.retryCount]
      
      // Джиттер для избежания "thundering herd"
      let jitter = 0
      if (context.retryCount > 1 && context.retryCount < 5) {
        jitter = Math.random() * 500
      } else if (context.retryCount >= 5) {
        jitter = Math.random() * 1000
      }

      const delay = Math.min(baseDelay + jitter, MAX_RETRY_DELAY)
      
      console.log(`[MobileReconnectPolicy] Retry ${context.retryCount + 1}/${retryDelays.length}, delay: ${Math.round(delay)}ms`)
      
      return delay
    },
  }
}
